<template>
  <div class="flex items-center gap-1 lg:hidden">
    <!-- Notification bell (mobile, outside menu) -->
    <NotificationBell v-if="isAuthenticated" />

    <!-- Hamburger button -->
    <button @click="mobileMenuOpen = !mobileMenuOpen"
      class="hamburger-btn"
      :aria-expanded="mobileMenuOpen" aria-label="Toggle navigation menu">
      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M4 6h16M4 12h16M4 18h16" />
        <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>

    <!-- Slide-down menu (teleported to body to avoid stacking context issues) -->
    <Teleport to="body">
      <div v-if="mobileMenuOpen" class="mobile-menu">
        <div class="mobile-menu-inner">

          <!-- Nav links -->
          <router-link
            v-for="item in NAVIGATION_ITEMS"
            :key="item.id"
            :to="item.path"
            @click="mobileMenuOpen = false"
            class="menu-link"
            :class="{ active: isNavigationItemActive(item, route.path) }">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="item.icon" />
            </svg>
            {{ item.label }}
          </router-link>

          <!-- Divider -->
          <div class="menu-divider"></div>

          <!-- Group selector -->
          <div v-if="isAuthenticated && currentGroup && userGroups" class="group-selector">
            <div v-if="(userGroups as any[]).length > 1">
              <label class="group-label">Active Group</label>
              <select
                :value="currentGroup.active_group.id"
                @change="handleGroupChange(Number(($event.target as HTMLSelectElement).value))"
                class="group-select">
                <option v-for="group in (userGroups as any[])" :key="group.id" :value="group.id">
                  {{ group.name }}
                </option>
              </select>
            </div>
            <div v-else class="group-badge-single">
              {{ currentGroup.active_group.name }}
            </div>
          </div>

          <!-- Profile link -->
          <router-link v-if="isAuthenticated && currentUser" to="/user" @click="mobileMenuOpen = false"
            class="menu-link">
            <div class="menu-avatar">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="#4F8646">
                <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-7 2-7 3v1h14v-1c0-1-3-3-7-3z"/>
              </svg>
            </div>
            {{ currentUser.first_name }} · Profile
          </router-link>

          <!-- Help link -->
          <router-link v-if="isAuthenticated" to="/help" @click="mobileMenuOpen = false" class="menu-link">
            <div class="menu-avatar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F8646" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            Help &amp; getting started
          </router-link>

          <!-- Logout -->
          <button v-if="isAuthenticated" @click="$emit('logout'); mobileMenuOpen = false"
            class="menu-logout-btn">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>

          <!-- Login / Register (unauthenticated) -->
          <template v-if="!isAuthenticated">
            <router-link to="/login" @click="mobileMenuOpen = false" class="menu-link">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Login
            </router-link>
            <router-link to="/register" @click="mobileMenuOpen = false" class="menu-link register">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Register
            </router-link>
          </template>

        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import type { User, Group, UserActiveGroup } from '../../services/authService'
import { NAVIGATION_ITEMS, isNavigationItemActive } from '../../config/navigation'
import NotificationBell from '../notifications/NotificationBell.vue'

interface Props {
  isAuthenticated: boolean
  currentUser: User | null
  currentGroup: UserActiveGroup | null
  userGroups: readonly Group[] | null
}

defineProps<Props>()

const emit = defineEmits<{
  logout: []
  groupChange: [groupId: number]
}>()

const route = useRoute()
const mobileMenuOpen = ref(false)

const handleGroupChange = (groupId: number) => {
  emit('groupChange', groupId)
}
</script>

<style scoped>
.hamburger-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748B;
  transition: background .15s;
}
.hamburger-btn:hover { background: #F1F5F9; }

.mobile-menu {
  position: fixed;
  left: 0;
  right: 0;
  top: 60px;
  background: white;
  border-top: 1px solid #F1F5F9;
  box-shadow: 0 8px 24px rgba(0,0,0,.08);
  z-index: 190;
}
.mobile-menu-inner { padding: 12px 16px 20px; }

.menu-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  text-decoration: none;
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: #334155;
  margin-bottom: 2px;
  transition: background .15s;
}
.menu-link:hover    { background: #F8FAFC; }
.menu-link.active   { background: #EDF3EC; color: #4F8646; font-weight: 600; }
.menu-link.register { color: #4F8646; font-weight: 600; }

.menu-divider { height: 1px; background: #F1F5F9; margin: 12px 0; }

.group-selector { padding: 0 14px; margin-bottom: 8px; }
.group-label {
  font-size: 11px;
  font-weight: 700;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: .8px;
  display: block;
  margin-bottom: 6px;
}
.group-select {
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
  background: white;
  padding: 0 12px;
  font-size: 13px;
  font-family: Inter, sans-serif;
  color: #334155;
  outline: none;
}
.group-badge-single {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  background: #F1F5F9;
  padding: 3px 10px;
  border-radius: 9999px;
  display: inline-block;
}

.menu-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #EDF3EC;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.menu-logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  color: #BA1A1A;
  cursor: pointer;
  transition: background .15s;
}
.menu-logout-btn:hover { background: rgba(186,26,26,.06); }
</style>
